"use client";

import { ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { ToastProvider } from "../ui/Toast";

interface AuthLayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  /**
   * Brand copy for the auth shell. `tagline` shows in the left panel (desktop) and
   * bottom-left below the form (mobile); `sub` + `foot` are desktop brand-panel only.
   */
  brand?: { tagline?: string; sub?: string; foot?: string };
  /** Drops the mobile tagline — for long forms that already fill the screen. */
  hideMobileTagline?: boolean;
}

function AuthLayout({
  children,
  title,
  description,
  brand,
  hideMobileTagline,
}: AuthLayoutProps) {
  return (
    <ToastProvider>
      <div className="flex min-h-screen bg-background">
        {/* Brand panel — desktop only */}
        <aside
          className="relative hidden basis-[45%] shrink-0 grow-0 flex-col items-start justify-between overflow-hidden border-r border-border px-14 py-12 lg:flex"
          style={{
            background:
              "radial-gradient(75% 85% at 14% 20%, rgba(0,214,143,0.15), transparent 62%), #101010",
          }}
        >
          <Link href="/" className="inline-flex">
            <Image
              src="/venuer-logo.png"
              alt="Venuer"
              width={140}
              height={33}
              className="h-auto w-[140px] opacity-95"
              priority
            />
          </Link>
          {(brand?.tagline || brand?.sub || brand?.foot) && (
            <div className="max-w-[420px]">
              {brand?.tagline && (
                <p className="text-balance font-display text-[34px] font-semibold leading-[1.18] tracking-[-0.025em] text-foreground">
                  {brand.tagline}
                </p>
              )}
              {brand?.sub && (
                <p className="mt-4 max-w-[360px] text-[15px] leading-[1.55] text-muted">
                  {brand.sub}
                </p>
              )}
              {brand?.foot && (
                <p className="mt-[30px] font-mono text-[11px] tracking-[0.06em] text-[var(--color-text-disabled)]">
                  {brand.foot}
                </p>
              )}
            </div>
          )}
        </aside>

        {/* Form panel */}
        <main className="relative flex flex-1 flex-col px-6 py-9 lg:flex-row lg:items-center lg:justify-center lg:px-8 lg:py-10">
          {/* Mobile-only ambient glow */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 lg:hidden"
            style={{
              background:
                "radial-gradient(85% 42% at 6% 4%, rgba(0,214,143,0.15), transparent 62%)",
            }}
          />

          <div className="relative mx-auto w-full max-w-[380px]">
            {/* Mobile-only logo */}
            <Link href="/" className="mb-14 flex w-fit lg:hidden">
              <Image
                src="/venuer-logo.png"
                alt="Venuer"
                width={110}
                height={26}
                className="h-[26px] w-auto"
                priority
              />
            </Link>

            {(title || description) && (
              <div className="mb-7">
                {title && (
                  <h1 className="font-display text-[30px] font-bold leading-[1.2] tracking-[-0.024em] text-foreground">
                    {title}
                  </h1>
                )}
                {description && (
                  <p className="mt-2 text-sm text-muted">{description}</p>
                )}
              </div>
            )}

            {children}
          </div>

          {/* Mobile-only tagline — pinned bottom-left */}
          {brand?.tagline && !hideMobileTagline && (
            <p className="relative mt-auto max-w-[16ch] text-balance pt-14 font-display text-[19px] font-semibold leading-[1.25] tracking-[-0.02em] text-foreground lg:hidden">
              {brand.tagline}
            </p>
          )}
        </main>
      </div>
    </ToastProvider>
  );
}

export { AuthLayout, type AuthLayoutProps };
